/**
 * Photo sync service — manages Supabase Storage upload/download for photos.
 *
 * Photos are stored locally as Dexie blobs and uploaded to Supabase Storage
 * at the path `{userId}/{photoId}.jpg`. Lazy upload: photos without a
 * `remoteUrl` are pending upload and will be retried on each sync cycle.
 *
 * Download is on-demand per record via downloadPhoto() — M81 hydration path.
 * Migration path: migrateLegacyPhotos() uploads all pre-M28 photos.
 */

import { supabase } from './supabase';
import { db as _prodDb } from './storage/db';
import { recordPendingDelete } from './sync-service';
import { emitPhotoMutation } from './storage/dexie-adapter';
import type { PhotoRecord } from '@/types/photo';

// ---------------------------------------------------------------------------
// Named constants (RULE-153)
// ---------------------------------------------------------------------------

/** Maximum simultaneous Supabase Storage downloads across the app. */
export const PHOTO_DOWNLOAD_CONCURRENCY = 4;

/** Retry budget per photo on network/5xx failure. */
export const PHOTO_DOWNLOAD_RETRY_MAX = 2;

/** Base delay (ms) for exponential backoff between retries. */
export const PHOTO_DOWNLOAD_RETRY_BASE_MS = 400;

/** Per-request timeout (ms) before treating the fetch as a retryable error. */
export const PHOTO_DOWNLOAD_TIMEOUT_MS = 20_000;

// ---------------------------------------------------------------------------
// PhotoHydrationError
// ---------------------------------------------------------------------------

export type PhotoHydrationErrorKind =
  | 'no-remote-url'
  | 'network'
  | 'unauthorized'
  | 'not-found'
  | 'unknown';

export class PhotoHydrationError extends Error {
  readonly kind: PhotoHydrationErrorKind;

  constructor(message: string, kind: PhotoHydrationErrorKind = 'unknown') {
    super(message);
    this.name = 'PhotoHydrationError';
    this.kind = kind;
  }
}

// ---------------------------------------------------------------------------
// Narrow db interface — injectable for testing (ADDITIONAL FIX a)
// ---------------------------------------------------------------------------

/**
 * Minimal photo-table shape required by this service.
 * Narrower than the full Dexie db type, so tests can inject a plain mock object
 * without constructing the entire Dexie instance. Tests use setPhotoDbForTesting();
 * production code always uses the real Dexie db imported above.
 */
export interface PhotoDb {
  photos: {
    where(field: string): { equals(v: string): { toArray(): Promise<PhotoRecord[]> } };
    get(id: string): Promise<PhotoRecord | undefined | null>;
    put(record: PhotoRecord): Promise<unknown>;
  };
  logEntries: {
    get(id: string): Promise<{ id: string; photos?: string[] } | undefined | null>;
  };
}

let _db: PhotoDb = _prodDb as unknown as PhotoDb;

/**
 * Inject a test double for the photo db tables.
 * Call in beforeEach / afterEach of unit tests. Never call in production code.
 */
export function setPhotoDbForTesting(testDb: PhotoDb | null): void {
  _db = testDb ?? (_prodDb as unknown as PhotoDb);
}

// ---------------------------------------------------------------------------
// Photo-hydrated callback (RULE-081: services never import stores)
// ---------------------------------------------------------------------------

/**
 * Module-level callback invoked after each successful downloadPhoto() blob write.
 * Registered at app startup by the caller (App.tsx) to avoid a circular dependency:
 *   photo-sync-service → sync-store → ... → photo-sync-service.
 *
 * Wiring pattern follows the same model as SyncOpts.onEntityStart in sync-service.ts.
 */
let _onPhotoHydrated: (() => void) | null = null;

/**
 * Register the callback that fires after each photo blob is written to Dexie.
 * Call this once at app startup with `useSyncStore.getState().decrementPhotoHydrationCount`.
 */
export function setPhotoHydratedCallback(cb: (() => void) | null): void {
  _onPhotoHydrated = cb;
}

// ---------------------------------------------------------------------------
// Concurrency semaphore (module-scoped)
// ---------------------------------------------------------------------------

/** FIFO queue of pending slots waiting to acquire a semaphore slot. */
const semaphoreWaiters: Array<() => void> = [];
let semaphoreCount = 0;

/**
 * Returns the count of active semaphore slots (photos currently being downloaded).
 * Exposed for production-visible concurrency assertions (RULE-157).
 */
export function getInflightDownloadCount(): number {
  return semaphoreCount;
}

/**
 * Reset semaphore state between unit tests.
 * Module-level semaphore leaks between test cases when a test is aborted mid-download.
 * Call in beforeEach of any test file that exercises downloadPhoto().
 * Never call in production code.
 */
export function resetSemaphoreForTesting(): void {
  semaphoreCount = 0;
  semaphoreWaiters.length = 0;
}

async function acquireSemaphore(): Promise<void> {
  if (semaphoreCount < PHOTO_DOWNLOAD_CONCURRENCY) {
    semaphoreCount++;
    return;
  }
  // Queue the caller as a waiter — FIFO
  return new Promise<void>((resolve) => {
    semaphoreWaiters.push(resolve);
  });
}

function releaseSemaphore(): void {
  const next = semaphoreWaiters.shift();
  if (next) {
    // A waiter is released — don't decrement/re-increment, just hand off
    next();
  } else {
    semaphoreCount--;
  }
}

const BUCKET = 'photos'; // renamed from hunthq-photos by D-03

function storagePath(userId: string, photoId: string): string {
  return `${userId}/${photoId}.jpg`;
}

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

/**
 * Upload all photos for a user that haven't been uploaded yet (remoteUrl is absent).
 * Individual failures are logged and skipped — they will retry on the next sync.
 */
export async function uploadPendingPhotos(userId: string): Promise<void> {
  const photos = await _db.photos.where('userId').equals(userId).toArray();
  const pending = photos.filter((p) => !p.remoteUrl);

  for (const photo of pending) {
    try {
      const path = storagePath(userId, photo.id);

      // Skip if no local bytes — defensive (filter above should exclude these).
      if (!photo.blobBytes) {
        console.warn('[photo-sync] Skipping upload — no local bytes for photo', photo.id);
        continue;
      }

      // Reconstruct the Blob in-memory at the upload boundary. This Blob never
      // round-trips through IDB, so the iOS Safari WebKit defect does not apply.
      const uploadType = photo.blobType ?? 'image/jpeg';
      const uploadBlob = new Blob([photo.blobBytes], { type: uploadType });
      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(path, uploadBlob, { contentType: uploadType, upsert: true });

      if (error) throw new Error(error.message);

      const now = new Date().toISOString();
      await _db.photos.put({
        ...photo,
        remoteUrl: path,
        uploadedAt: now,
        updatedAt: now,
      });
    } catch (err) {
      console.error('[photo-sync] Failed to upload photo', photo.id, err);
      // Continue — skip this photo, retry next cycle
    }
  }
}

// ---------------------------------------------------------------------------
// Download (on-demand, single record)
// ---------------------------------------------------------------------------

/**
 * Download a single photo blob from Supabase Storage and write it back to Dexie.
 *
 * Behavior:
 * 1. If blob is already present locally — return it immediately (no network I/O).
 * 2. If remoteUrl is absent — throw PhotoHydrationError('no-remote-url').
 * 3. Acquire a semaphore slot (cap: PHOTO_DOWNLOAD_CONCURRENCY).
 * 4. Fetch blob via Supabase Storage .download(remoteUrl) with AbortController timeout.
 * 5. Retry up to PHOTO_DOWNLOAD_RETRY_MAX times on network errors or 5xx.
 *    4xx errors fail immediately (unauthorized/not-found).
 * 6. Write blob back to Dexie via db.photos.put (also stamps updatedAt).
 * 7. Release semaphore slot in finally.
 */
export async function downloadPhoto(photoId: string): Promise<Blob> {
  const photo = await _db.photos.get(photoId);

  if (photo?.blobBytes) {
    // Reconstruct Blob at the read boundary — no IDB-Blob round-trip per ADR-044.
    return new Blob([photo.blobBytes], { type: photo.blobType ?? 'image/jpeg' });
  }

  if (!photo?.remoteUrl) {
    throw new PhotoHydrationError(
      `downloadPhoto: no remoteUrl for photo ${photoId} — photo was never uploaded`,
      'no-remote-url',
    );
  }

  const { remoteUrl } = photo;

  await acquireSemaphore();
  try {
    let lastError: PhotoHydrationError | null = null;

    for (let attempt = 0; attempt <= PHOTO_DOWNLOAD_RETRY_MAX; attempt++) {
      if (attempt > 0) {
        await new Promise<void>((resolve) =>
          setTimeout(resolve, PHOTO_DOWNLOAD_RETRY_BASE_MS * 2 ** (attempt - 1)),
        );
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), PHOTO_DOWNLOAD_TIMEOUT_MS);

      try {
        const { data, error } = await supabase.storage.from(BUCKET).download(remoteUrl);

        clearTimeout(timeoutId);

        if (error) {
          // Parse error kind from Supabase error message
          const msg = typeof error === 'object' && 'message' in error
            ? String((error as { message: unknown }).message)
            : String(error);

          if (msg.includes('401') || msg.includes('unauthorized') || msg.includes('403')) {
            throw new PhotoHydrationError(
              `downloadPhoto(${photoId}): ${msg}`,
              'unauthorized',
            );
          }
          if (msg.includes('404') || msg.includes('not found')) {
            throw new PhotoHydrationError(
              `downloadPhoto(${photoId}): ${msg}`,
              'not-found',
            );
          }
          // Treat as retryable network/server error
          lastError = new PhotoHydrationError(
            `downloadPhoto(${photoId}): ${msg}`,
            'network',
          );
          continue;
        }

        if (!data) {
          lastError = new PhotoHydrationError(
            `downloadPhoto(${photoId}): Supabase returned no data`,
            'network',
          );
          continue;
        }

        // Success — write bytes back to Dexie per ADR-044 §E convert-before-write.
        // The freshly-downloaded Blob is iOS-safe (never round-tripped IDB), so
        // calling .arrayBuffer() here does not trigger the WebKit defect.
        const freshPhoto = await _db.photos.get(photoId);
        if (freshPhoto) {
          const bytes = await data.arrayBuffer();
          await _db.photos.put({
            ...freshPhoto,
            blobBytes: bytes,
            blobType: data.type || 'image/jpeg',
            blobSize: bytes.byteLength,
            updatedAt: new Date().toISOString(),
          });
          emitPhotoMutation(photoId);
        }

        // Notify caller (sync-store) so InitialSyncBoundary counter stays accurate.
        // Callback is null until App.tsx registers it — guard against early calls.
        _onPhotoHydrated?.();

        return data;
      } catch (err) {
        clearTimeout(timeoutId);

        if (err instanceof PhotoHydrationError) {
          // 4xx — no retry
          if (err.kind === 'unauthorized' || err.kind === 'not-found') throw err;
          lastError = err;
        } else if (err instanceof Error && err.name === 'AbortError') {
          lastError = new PhotoHydrationError(
            `downloadPhoto(${photoId}): timed out after ${PHOTO_DOWNLOAD_TIMEOUT_MS}ms`,
            'network',
          );
        } else {
          lastError = new PhotoHydrationError(
            `downloadPhoto(${photoId}): ${String(err)}`,
            'network',
          );
        }
      }
    }

    throw lastError ?? new PhotoHydrationError(
      `downloadPhoto(${photoId}): exhausted retries`,
      'network',
    );
  } finally {
    releaseSemaphore();
  }
}

// ---------------------------------------------------------------------------
// Download (on-demand, per log entry — legacy bulk)
// ---------------------------------------------------------------------------

/**
 * Download all photos for a log entry from Supabase Storage into Dexie.
 * Called when a new device opens a LogEntry that has photo IDs but no local blobs.
 */
export async function downloadPhotosForEntry(
  logEntryId: string,
  userId: string,
): Promise<void> {
  const entry = await _db.logEntries.get(logEntryId);
  if (!entry || !entry.photos || entry.photos.length === 0) return;

  for (const photoId of entry.photos) {
    const existing = await _db.photos.get(photoId);
    if (existing?.blobBytes) continue; // already have it locally

    try {
      const path = storagePath(userId, photoId);
      const { data, error } = await supabase.storage.from(BUCKET).download(path);
      if (error) throw new Error(error.message);
      if (!data) continue;

      const now = new Date().toISOString();
      // Convert-before-write per ADR-044 §E — fresh Blob is iOS-safe; never persist Blob to IDB.
      const bytes = await data.arrayBuffer();
      await _db.photos.put({
        id: photoId,
        logEntryId,
        userId,
        blobBytes: bytes,
        blobType: data.type || 'image/jpeg',
        blobSize: bytes.byteLength,
        mimeType: 'image/jpeg',
        width: 0,   // dimensions not available on remote-only photo
        height: 0,
        sizeBytes: data.size,
        createdAt: now,
        remoteUrl: path,
        uploadedAt: now,
        updatedAt: now,
      });
      emitPhotoMutation(photoId);
    } catch (err) {
      console.error('[photo-sync] Failed to download photo', photoId, err);
    }
  }
}

// ---------------------------------------------------------------------------
// Delete (remote)
// ---------------------------------------------------------------------------

/**
 * Remove a photo object from Supabase Storage.
 * Called when processing a 'photo' entry in pendingDeletes.
 */
export async function deleteRemotePhoto(photoId: string, userId: string): Promise<void> {
  const path = storagePath(userId, photoId);
  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error) throw new Error(`[photo-sync] deleteRemotePhoto(${photoId}): ${error.message}`);
}

// ---------------------------------------------------------------------------
// Legacy migration (one-time, idempotent)
// ---------------------------------------------------------------------------

/**
 * Upload all existing photos from Dexie to Supabase Storage.
 * Safe to call multiple times — already-uploaded photos (have remoteUrl) are skipped.
 */
export async function migrateLegacyPhotos(userId: string): Promise<void> {
  await uploadPendingPhotos(userId);
}

// ---------------------------------------------------------------------------
// photo-service integration helpers
// ---------------------------------------------------------------------------

/**
 * Queue a photo for remote deletion. Call after hard-deleting from Dexie.
 * Uses the generic pendingDeletes table with entityType 'photo'.
 */
export async function queuePhotoDelete(photoId: string): Promise<void> {
  await recordPendingDelete(photoId, 'photo');
}
